import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  SxProps,
  Typography
} from '@mui/material';
import { FC, ReactNode } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type SectionProps = {
  children: ReactNode;
  title?: string;
  actionButtons?: ReactNode;
  collapsible?: boolean;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  sx?: SxProps;
};

export const Section: FC<SectionProps> = ({
  children,
  title,
  actionButtons,
  collapsible,
  expanded,
  onToggle,
  sx
}) => {
  const headerContent = title && <SectionHeader title={title} />;

  if (!collapsible) {
    return (
      <Paper
        sx={{
          p: 2,
          mb: 1.2,
          borderRadius: 2,
          ...sx
        }}
      >
        {headerContent}
        {actionButtons && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            {actionButtons}
          </Box>
        )}
        {children}
      </Paper>
    );
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle ? (_, isExpanded) => onToggle(isExpanded) : undefined}
      sx={{
        mb: 1.2,
        borderRadius: 2,
        '&::before': {
          display: 'none'
        },
        '&.Mui-expanded': {
          my: '0.6rem'
        },
        '&:first-of-type': {
          borderRadius: '8px 8px 8px 8px'
        },
        '&:last-of-type': {
          borderRadius: '8px 8px 8px 8px'
        },

        ...sx
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {headerContent}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        {actionButtons && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            {actionButtons}
          </Box>
        )}
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

type SectionHeaderProps = {
  title: string;
  sx?: SxProps;
};

export const SectionHeader: FC<SectionHeaderProps> = ({ title, sx }) => {
  return (
    <Typography
      sx={{
        color: 'text.secondary',
        opacity: 0.9,
        fontSize: '1.1rem',
        fontWeight: 500,
        ...sx
      }}
    >
      {title}
    </Typography>
  );
};
